<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import { ArrowRight, CalendarClock, CircleAlert } from "lucide-vue-next";
import { useBriefStore } from "@/stores/briefs";
import { useProjectStore } from "@/stores/projects";
import ProjectProgressBar from "@/modules/progress/ProjectProgressBar.vue";
import ProjectTimelineView from "@/modules/progress/ProjectTimelineView.vue";

const emit = defineEmits<{ openBrief: [id: number] }>();
const briefStore = useBriefStore();
const projects = useProjectStore();
const filter = ref("all");
const timelineProjectId = ref<number | null>(null);
let refreshTimer: number | undefined;

const tabs = computed(() => [
  { id: "all", label: "全部", count: projects.dashboard?.counts.all || 0 },
  { id: "draft", label: "草稿", count: projects.dashboard?.counts.drafts || 0 },
  { id: "active", label: "执行中", count: projects.dashboard?.counts.active || 0 },
  {
    id: "acceptance",
    label: "待我验收",
    count: projects.dashboard?.counts.awaitingAcceptance || 0,
  },
  {
    id: "completed",
    label: "已完成",
    count: projects.dashboard?.counts.completed || 0,
  },
]);
const visibleProjects = computed(() =>
  (projects.dashboard?.projects || []).filter((project) => {
    if (filter.value === "all") return true;
    if (filter.value === "draft") return project.status === "draft";
    if (filter.value === "active")
      return ["submitted", "in_progress"].includes(project.status);
    if (filter.value === "acceptance")
      return project.lifecycleState === "awaiting_acceptance";
    return project.status === "accepted";
  }),
);
const stageLabel = (stage: string) =>
  ({
    admin_review: "管理员分析中",
    admin_client_review: "等待你确认管理员方案",
    admin_confirmed: "已确认，等待分派",
    admin_revision: "方案调整中",
    assigned: "已分派团队",
    intake: "需求沟通",
    drafting: "方案整理",
    solution_drafting: "方案整理",
    solution_review: "方案待确认",
    project_draft: "项目草稿",
    submitted: "已提交",
    team_accepted: "团队已接单",
    scheduled: "已排期",
    in_progress: "执行中",
    awaiting_acceptance: "等待验收",
    revision: "修改中",
    accepted: "已完成",
    completed: "已完成",
  })[stage] || stage;

async function load() {
  await Promise.all([projects.loadDashboard(), briefStore.loadList()]);
}
async function showTimeline(briefId: number) {
  timelineProjectId.value = timelineProjectId.value === briefId ? null : briefId;
  if (timelineProjectId.value) await projects.loadTimeline(briefId);
}
onMounted(() => {
  void load();
  refreshTimer = window.setInterval(() => void load(), 10000);
});
onUnmounted(() => {
  if (refreshTimer) window.clearInterval(refreshTimer);
});
</script>

<template>
  <div class="dashboard">
    <header class="page-head">
      <div><h1>项目工作台</h1><p>查看项目进度、时间计划、阻塞和待处理事项。</p></div>
      <button class="btn btn--primary" @click="emit('openBrief', 0)">新建需求</button>
    </header>
    <nav class="segments" aria-label="项目筛选">
      <button v-for="tab in tabs" :key="tab.id" :class="{ active: filter === tab.id }" @click="filter = tab.id">
        {{ tab.label }}<span>{{ tab.count }}</span>
      </button>
    </nav>
    <section class="project-list">
      <article v-for="project in visibleProjects" :key="project.id" class="project-row">
        <button class="project-main" @click="emit('openBrief', project.id)">
          <span class="project-title"><b>{{ project.title }}</b><small>{{ stageLabel(project.lifecycleState) }}</small></span>
          <ProjectProgressBar :progress="project.progress" compact />
          <span class="project-date"><CalendarClock :size="16" />{{ project.progress.plannedEndAt || "待团队评估日期" }}</span>
          <span v-if="project.progress.blockedItems" class="project-alert"><CircleAlert :size="16" />{{ project.progress.blockedItems }} 项阻塞</span>
          <span v-else class="project-next">{{ project.progress.nextActor === "client" ? "等待你处理" : "团队处理中" }}</span>
          <ArrowRight :size="18" />
        </button>
        <button class="timeline-toggle" @click="showTimeline(project.id)">
          {{ timelineProjectId === project.id ? "收起时间轴" : "查看时间轴" }}
        </button>
        <ProjectTimelineView v-if="timelineProjectId === project.id" :timeline="projects.timeline" />
      </article>
      <p v-if="!visibleProjects.length" class="empty">当前分类下还没有项目。</p>
    </section>
  </div>
</template>

<style scoped>
.dashboard{min-height:calc(100vh - 64px);background:#f5f7f7}.page-head{display:flex;align-items:center;justify-content:space-between;gap:20px;padding:26px 30px;background:#fff;border-bottom:1px solid var(--line)}.page-head h1{margin:0;font-size:24px}.page-head p{margin:4px 0 0;color:var(--muted)}.segments{display:flex;gap:0;padding:18px 30px 0;overflow:auto}.segments button{display:flex;align-items:center;gap:7px;min-height:38px;border:1px solid var(--line);border-right:0;padding:7px 12px;background:#fff;white-space:nowrap}.segments button:last-child{border-right:1px solid var(--line)}.segments button.active{background:var(--ink);color:#fff}.segments span{display:grid;min-width:20px;height:20px;place-items:center;background:#edf1f1;color:var(--muted);font-size:11px}.segments .active span{background:#fff;color:var(--ink)}.project-list{display:grid;gap:10px;padding:16px 30px 30px}.project-row{padding:0 16px;background:#fff;border:1px solid var(--line)}.project-main{display:grid;grid-template-columns:minmax(190px,1.3fr) minmax(180px,1fr) minmax(150px,.8fr) 120px 18px;align-items:center;gap:18px;width:100%;min-height:92px;border:0;background:transparent;text-align:left}.project-title b,.project-title small{display:block}.project-title b{font-size:16px}.project-title small{margin-top:5px;color:var(--brand-dark)}.project-date,.project-alert{display:flex;align-items:center;gap:7px;color:var(--muted);font-size:12px}.project-alert{color:var(--danger)}.project-next{color:var(--muted);font-size:12px}.timeline-toggle{margin:0 0 12px;border:0;background:transparent;color:var(--info);font-size:12px}.project-row :deep(.timeline){padding:15px 0;border-top:1px solid var(--line)}.empty{padding:50px;text-align:center;color:var(--muted);background:#fff;border:1px solid var(--line)}@media(max-width:1000px){.project-main{grid-template-columns:minmax(180px,1fr) minmax(160px,1fr) 18px}.project-date,.project-alert,.project-next{display:none}}@media(max-width:700px){.page-head{padding:18px 14px}.segments{padding:14px 14px 0}.project-list{padding:12px 14px 24px}.project-row{padding:0 12px}.project-main{grid-template-columns:minmax(0,1fr) 18px;gap:10px;padding:13px 0}.project-main :deep(.progress){grid-column:1/-1;grid-row:2}.project-title b{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}}
.page-head .btn{flex:0 0 auto;white-space:nowrap}.segments{scrollbar-width:none}.segments::-webkit-scrollbar{display:none}
</style>
